import { useForm, useList, useApiUrl } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useCallback, useMemo, useRef } from 'react';
import {
  FileText,
  User,
  Loader2,
  Image as ImageIcon,
  X,
  Plus,
  GripVertical,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RichTextEditor, isHtmlEmpty } from '@/components/editor';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormHeader, FormField, FormStickyActions, FormSpacer } from '@/components/forms';
import { cn } from '@/lib/utils';
import { getActiveTenant } from '@/lib/active-tenant';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Post, TeacherOption } from './types';

interface PostFormImage {
  fileId: number;
  url: string;
  order: number;
}

function getTeacherName(teacher: TeacherOption): string {
  const ruTranslation = teacher.translations?.find((t) => t.languageCode === 'ru');
  if (ruTranslation?.name) { return ruTranslation.name; }
  const firstTranslation = teacher.translations?.[0];
  if (firstTranslation?.name) { return firstTranslation.name; }
  return `Учитель #${teacher.id}`;
}

function countWords(html: string): number {
  const text = html.replaceAll(/<[^>]*>/g, '').trim();
  if (!text) { return 0; }
  return text.split(/\s+/).length;
}

// --- Sortable image for DnD ---

interface SortableImageCardProps {
  image: PostFormImage;
  index: number;
  onRemove: (index: number) => void;
}

function SortableImageCard({ image, index, onRemove }: SortableImageCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `img-${image.fileId}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative group rounded-lg overflow-hidden border bg-muted/30 aspect-square',
        isDragging && 'opacity-50 z-50'
      )}
    >
      <img
        src={image.url}
        alt={`Изображение ${index + 1}`}
        className="w-full h-full object-cover"
      />
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 p-1 bg-black/50 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="h-4 w-4 text-white" />
      </div>
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="absolute top-2 right-2 p-1 bg-destructive rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/90"
      >
        <X className="h-4 w-4 text-white" />
      </button>
      <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/50 rounded text-xs text-white">
        {index + 1}
      </div>
    </div>
  );
}

// --- Main Form ---

export function PostsForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const apiUrl = useApiUrl();

  // Form state
  const [content, setContent] = useState('');
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [status, setStatus] = useState<'DRAFT' | 'PUBLISHED'>('DRAFT');
  const [images, setImages] = useState<PostFormImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [formReady, setFormReady] = useState(!isEdit);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load teachers
  const { result: teachersResult } = useList<TeacherOption>({
    resource: 'teachers',
    pagination: { pageSize: 100, mode: 'server' },
    filters: [{ field: 'isActive', operator: 'eq', value: true }],
  });
  const teachers = teachersResult.data ?? [];

  // Refine form for create/update
  const {
    formLoading,
    onFinish,
    query: formQuery,
  } = useForm<Post>({
    resource: 'posts',
    action: isEdit ? 'edit' : 'create',
    id: isEdit ? id : undefined,
    redirect: 'list',
    onMutationSuccess: () => {
      navigate('/posts');
    },
  });

  // Populate form when editing
  const post = formQuery?.data?.data;
  const hasPopulatedRef = useRef(false);

  if (isEdit && post && !hasPopulatedRef.current) {
    hasPopulatedRef.current = true;
    setContent(post.content);
    setStatus(post.status === 'PUBLISHED' ? 'PUBLISHED' : 'DRAFT');
    setImages(
      (post.images ?? []).map((img) => ({
        fileId: img.fileId,
        url: img.url,
        order: img.order,
      }))
    );
    setFormReady(true);
  }

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Upload file
  const getAuthToken = useCallback(() => localStorage.getItem('accessToken'), []);

  const uploadFile = useCallback(
    async (file: File): Promise<{ id: number; url: string }> => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${apiUrl}/upload/file`);

        const token = getAuthToken();
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        // X-Tenant-Id: XHR минует global fetch interceptor — добавляем вручную,
        // иначе на узб (club) upload падал в default-тенант.
        const tenant = getActiveTenant();
        if (tenant) {
          xhr.setRequestHeader('X-Tenant-Id', tenant);
        }

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200 || xhr.status === 201) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.success && response.data?.id && response.data?.url) {
                resolve({ id: response.data.id, url: response.data.url });
              } else {
                reject(new Error(response.errors?.[0] ?? 'Ошибка загрузки'));
              }
            } catch {
              reject(new Error('Ошибка обработки ответа сервера'));
            }
          } else {
            reject(new Error(`Ошибка загрузки: ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Ошибка сети'));
        xhr.send(formData);
      });
    },
    [apiUrl, getAuthToken]
  );

  const handleFileSelect = useCallback(
    async (files: FileList) => {
      setUploadError(null);
      setIsUploading(true);
      setUploadProgress(0);

      for (const file of files) {
        if (images.length >= 10) { break; }

        if (!file.type.startsWith('image/')) {
          setUploadError('Пожалуйста, выберите изображение');
          continue;
        }

        if (file.size > 10 * 1024 * 1024) {
          setUploadError('Размер файла не должен превышать 10 МБ');
          continue;
        }

        try {
          const result = await uploadFile(file);
          setImages((prev) => [
            ...prev,
            { fileId: result.id, url: result.url, order: prev.length },
          ]);
        } catch (err) {
          setUploadError(err instanceof Error ? err.message : 'Ошибка загрузки');
        }
      }

      setIsUploading(false);
      setUploadProgress(0);
    },
    [images.length, uploadFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files);
      }
      e.target.value = '';
    },
    [handleFileSelect]
  );

  const handleRemoveImage = useCallback(
    (index: number) => {
      setImages((prev) =>
        prev.filter((_, i) => i !== index).map((img, i) => ({ ...img, order: i }))
      );
    },
    []
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        setImages((prev) => {
          const oldIndex = prev.findIndex((img) => `img-${img.fileId}` === active.id);
          const newIndex = prev.findIndex((img) => `img-${img.fileId}` === over.id);
          return arrayMove(prev, oldIndex, newIndex).map((img, i) => ({ ...img, order: i }));
        });
      }
    },
    []
  );

  // Submit
  const handleSubmit = useCallback(() => {
    if (isEdit) {
      onFinish({
        content,
        imageIds: images.map((img) => img.fileId),
      });
    } else {
      if (!selectedTeacherId || isHtmlEmpty(content)) { return; }
      onFinish({
        content,
        teacherId: Number(selectedTeacherId),
        status,
        imageIds: images.map((img) => img.fileId),
      });
    }
  }, [isEdit, content, images, selectedTeacherId, status, onFinish]);

  const canSubmit = useMemo(() => {
    if (isHtmlEmpty(content)) { return false; }
    if (!isEdit && !selectedTeacherId) { return false; }
    return true;
  }, [content, isEdit, selectedTeacherId]);

  if (isEdit && !formReady) {
    if (formQuery?.isError) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-destructive">Не удалось загрузить пост</p>
          <button
            type="button"
            onClick={() => navigate('/posts')}
            className="text-sm text-muted-foreground underline hover:text-foreground"
          >
            Вернуться к списку
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FormHeader
        title={isEdit ? 'Редактировать пост' : 'Создать пост'}
        description={isEdit ? 'Изменение содержимого поста' : 'Создание поста от имени учителя или администрации'}
        onBack={() => navigate('/posts')}
        sticky
        icon={<FileText className="h-5 w-5" />}
      />

      <div className="max-w-3xl space-y-6">
        {/* Автор (только при создании) */}
        {!isEdit && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4" />
                Автор
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FormField label="Учитель" name="teacherId" required>
                <Select value={selectedTeacherId} onValueChange={setSelectedTeacherId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите учителя..." />
                  </SelectTrigger>
                  <SelectContent>
                    {teachers.map((teacher) => (
                      <SelectItem key={teacher.id} value={teacher.id}>
                        <div className="flex items-center gap-2">
                          {teacher.photoUrl ? (
                            <img
                              src={teacher.photoUrl}
                              alt=""
                              className="w-5 h-5 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                              <User className="h-3 w-3 text-muted-foreground" />
                            </div>
                          )}
                          <span>{getTeacherName(teacher)}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </CardContent>
          </Card>
        )}

        {/* Контент */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              Содержание
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField label="Текст поста" name="content" required>
              <RichTextEditor
                value={content}
                onChange={setContent}
                placeholder="Напишите текст поста..."
              />
            </FormField>
            {content.trim().length > 0 && (
              <p className="text-xs text-muted-foreground">
                {countWords(content)} слов
              </p>
            )}
          </CardContent>
        </Card>

        {/* Изображения */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ImageIcon className="h-4 w-4" />
              Изображения
              {images.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {images.length}/10
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleInputChange}
              className="hidden"
            />

            {images.length > 0 && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={images.map((img) => `img-${img.fileId}`)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {images.map((image, index) => (
                      <SortableImageCard
                        key={image.fileId}
                        image={image}
                        index={index}
                        onRemove={handleRemoveImage}
                      />
                    ))}

                    {images.length < 10 && (
                      <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors border-muted-foreground/25 hover:border-primary/50"
                      >
                        {isUploading ? (
                          <div className="flex flex-col items-center gap-2 p-4">
                            <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
                            <Progress value={uploadProgress} className="h-1 w-full" />
                          </div>
                        ) : (
                          <>
                            <Plus className="h-6 w-6 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground mt-1">Добавить</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </SortableContext>
              </DndContext>
            )}

            {images.length === 0 && (
              <button
                type="button"
                onClick={() => !isUploading && inputRef.current?.click()}
                className={cn(
                  'w-full border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
                  'border-muted-foreground/25 hover:border-primary/50',
                  uploadError && 'border-destructive'
                )}
              >
                {isUploading ? (
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="h-10 w-10 text-muted-foreground animate-spin" />
                    <div className="w-full max-w-xs">
                      <Progress value={uploadProgress} className="h-2" />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Загрузка... {uploadProgress}%
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <ImageIcon className="h-10 w-10 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Нажмите для выбора изображений
                    </p>
                    <p className="text-xs text-muted-foreground/70">
                      Максимум 10 изображений, до 10 МБ каждое
                    </p>
                  </div>
                )}
              </button>
            )}

            {uploadError && (
              <p className="text-sm text-destructive">{uploadError}</p>
            )}

            {images.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Перетаскивайте изображения для изменения порядка
              </p>
            )}
          </CardContent>
        </Card>

        {/* Статус (только при создании) */}
        {!isEdit && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Статус публикации</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup value={status} onValueChange={(v) => setStatus(v as 'DRAFT' | 'PUBLISHED')}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="DRAFT" id="status-draft" />
                  <Label htmlFor="status-draft" className="cursor-pointer font-normal">
                    Черновик — пост сохранится, но не будет виден пользователям
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="PUBLISHED" id="status-published" />
                  <Label htmlFor="status-published" className="cursor-pointer font-normal">
                    Опубликовать сразу — пост будет виден всем пользователям
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
        )}

        <FormSpacer />
      </div>

      <FormStickyActions
        primaryLabel={isEdit ? 'Сохранить' : 'Создать пост'}
        pendingLabel={isEdit ? 'Сохранение...' : 'Создание...'}
        isPending={formLoading}
        onPrimary={handleSubmit}
        disabled={!canSubmit || isUploading}
        secondaryLabel="Отмена"
        onSecondary={() => navigate('/posts')}
      />
    </div>
  );
}
